import moment from 'moment';

import {FolderSummary} from 'constants/FolderConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {Batch} from 'constants/SampleConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {BioentitySummary} from 'folder-items/FileConstants';
import {EntryWithFulfillments, FromServerId, ToServerEntry, VersionSummary} from 'notebook/ELNConstants';
// @ts-expect-error
import {formatDate} from 'notebook/entries/EntryDayOps';
import {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import {
  RegistrationOperation,
  RegistrationTable,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import patchJSONString from 'notebook/util/patchJSONString';
import ContentWorkers from 'notebook/workers/ContentWorkers';
import {RequestFulfillmentDefinition} from 'requests/fulfillments/FulfillmentConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';
import {asJQueryPromise} from 'util/Promises';
import * as TaskRunner from 'util/TaskRunner';
import {RegistrationRecordList, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

interface BaseEntryCreationParams {
  name: string;
  folder: string;
  fulfillments?: Array<RequestFulfillmentDefinition>;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  studyIds?: Array<string>;
}

interface EntryUpdateResponse {
  cidIdMap: CidIdMap;
  cidNotePartProvenanceIdMap?: CidNotePartProvenanceIdMap;
  editURL: string;
  folder: FolderSummary;
  errors: Array<string>;
  lock?: ServerDocumentEditorLock;
}

export interface BlankEntryCreationParams extends BaseEntryCreationParams {
  initialDay?: moment.Moment;
  numDays?: number;
}

export interface EntryFromTemplateCreationParams extends BaseEntryCreationParams {
  initialDay?: string;
  template: string;
}

export default {
  create({
    name,
    folder,
    initialDay,
    numDays,
    fulfillments,
    tagSchemaId,
    schemaTags,
    studyIds,
  }: BlankEntryCreationParams): JQueryPromise<EntryWithFulfillments> {
    if (initialDay == null) {
      initialDay = moment();
    }
    const formattedInitialDay = formatDate(initialDay);
    if (numDays == null) {
      numDays = 1;
    }
    const data = {
      name,
      folder,
      initialDay: formattedInitialDay,
      numDays,
      fulfillments,
      tagSchemaId,
      schemaTags,
      studyIds,
    };
    return net.postJSON({url: '/1/api/entries/', data}).done(() => {
      FolderDAOCache.invalidateFolderContents(folder);
    });
  },

  delete(id: string): JQueryPromise<{}> {
    return net.delete_({url: `/1/api/entries/${id}`});
  },

  get(id: string): JQueryPromise<EntryWithFulfillments> {
    return net.getJSON(`/1/api/entries/${id}`, {view: true});
  },

  getVersions(
    id: string,
    param: {openVersionId: number}
  ): JQueryPromise<{
    data: Array<VersionSummary<FromServerId>>;
    parent: {
      id: string;
      versionId: number;
      name: string;
      modifiedAt: string;
    };
  }> {
    const {openVersionId} = param;
    return net.getJSON(`/1/api/entries/${id}/versions/`, {openVersionId});
  },

  update(
    id: string,
    data: ToServerEntry | {name?: string; folder_id?: string}
  ): JQueryPromise<EntryUpdateResponse> {
    const url = `/1/api/entries/${id}`;
    return asJQueryPromise(
      ContentWorkers.stringify(data).then((json) =>
        patchJSONString({url, data: json})
      ) as Promise<EntryUpdateResponse>
    );
  },

  getVersion(id: string, versionId: number): JQueryPromise<EntryWithFulfillments> {
    return net.getJSON(`/1/api/entries/${id}/versions/${versionId}`);
  },

  createFromTemplate({
    name,
    folder,
    template,
    initialDay,
    fulfillments,
    tagSchemaId,
    schemaTags,
    studyIds,
  }: EntryFromTemplateCreationParams): JQueryPromise<EntryWithFulfillments> {
    return net
      .postJSON({
        url: '/1/api/entries/create-from-template',
        data: {
          name,
          folder,
          template,
          initialDay: initialDay != null ? initialDay : formatDate(moment()),
          fulfillments,
          tagSchemaId,
          schemaTags,
          studyIds,
        },
      })
      .done(() => {
        FolderDAOCache.invalidateFolderContents(folder);
      });
  },

  recoverData({
    name,
    folderId,
    entry,
    apiId,
  }: {
    name: string;
    folderId: string;
    entry: ToServerEntry;
    apiId: string;
  }): JQueryPromise<EntryWithFulfillments> {
    return TaskRunner.postJSON({
      url: '/1/api/entries:recover',
      data: {name, folderId, entry, apiId},
    });
  },

  createRegistrationTable({
    name,
    tagSchemaId,
    tableType,
    labelingStrategy,
    fileId,
    isWatchingInputTable,
  }: {
    name: string;
    tagSchemaId: string;
    tableType: RegistrationTableType;
    labelingStrategy: RegistryLabelingStrategy;
    fileId: string;
    isWatchingInputTable: boolean;
  }): JQueryPromise<{
    registrationTable: RegistrationTable;
    registrationRecordList: RegistrationRecordList;
  }> {
    return net.postJSON({
      url: `/1/api/entries/registration-tables`,
      data: {
        name,
        tagSchemaId,
        tableType,
        labelingStrategy,
        fileId,
        isWatchingInputTable,
      },
    });
  },

  registerSamples(
    entryId: string,
    registrationTableProvenanceUuid: string,
    operations: Array<RegistrationOperation>,
    rowIndicesToRegister: Array<number>
  ): JQueryPromise<{
    affectedStageRuns: Array<StageRun>;
    stageRunSamples: Array<StageRunSample>;
    batchesById: {[batchId: string]: Batch};
    fileSummariesById: {[bioentityId: string]: BioentitySummary};
  }> {
    return net.postJSON({
      url: `/1/api/entries/${entryId}/register-samples`,
      data: {
        provenanceUuid: registrationTableProvenanceUuid,
        operations,
        rowIndicesToRegister,
      },
    });
  },
};
